import * as Persona from 'persona'
import * as React from 'react'
import { Locale } from '@doordash/lib-i18n'
import { IDVStatus } from '../../../../utilities/steps'
import * as actions from '../../../../actions'
import { connect } from 'react-redux'
import Loading from '@doordash/component-loading'
import { IdvChannel } from '../../../../components/molecules/identity-verification/types'
import { getIdvSessionToken } from '../../../../api'
import {
  EventActions,
  IdentityVerificationPages,
} from '../../../../typeModules/events'
import { camelCaseObjectKeys } from '@doordash/object-utils'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import * as Exp from '../../../../experimentConstants'
import { get } from 'lodash'
import {
  fromDoorstepInjectedEnvironment,
  isProduction,
} from '../../../../utilities/environment'
import { PERSONA_ENV, VehicleType } from '../../../../constants'
import { Application } from 'src/utilities/application/application'
import getCurrentLocale from '../../../../i18n'

type Props = {
  uniqueLink?: string
  vehicleType?: string
  trackIdentityVerificationSteps: (data: any) => any
  onUpdate?: any
  updateIdvStatus?: (
    uniqueLink: string,
    inquiryId: string,
    event: string,
    polling: boolean
  ) => Promise<any>
  idvStatus?: {
    value?: string
    isSubmitting?: boolean
    isSubmitted?: boolean
  }
  renderToggle: boolean
  email: string
  isIDVReferral: boolean
  application?: Application
}

export const TemplateIdTypes = {
  motorized: [
    'tmpl_kfaFkGugqG9jqh6aAuc21Vwd',
    'tmpl_7d8pqQoJhwLHL1x38nBfpCuu',
    'tmpl_WocVBd1qwFoAQQXx6V9SRpSK',
  ],
  'non-motorized': [
    'tmpl_dos19dD2bQ9wjrnVjAWRo1Ff',
    'tmpl_9pdz4FX6uwqATzxyQPTc1RYP',
  ],
}

export const TemplateIds = {
  referral: {
    car: 'tmpl_7d8pqQoJhwLHL1x38nBfpCuu',
    scooter: 'tmpl_7d8pqQoJhwLHL1x38nBfpCuu',
    motorcycle: 'tmpl_7d8pqQoJhwLHL1x38nBfpCuu',
    bicycle: 'tmpl_9pdz4FX6uwqATzxyQPTc1RYP',
    walking: 'tmpl_9pdz4FX6uwqATzxyQPTc1RYP',
    other: 'tmpl_9pdz4FX6uwqATzxyQPTc1RYP',
  },
  'non-referral': {
    car: 'tmpl_kfaFkGugqG9jqh6aAuc21Vwd',
    scooter: 'tmpl_kfaFkGugqG9jqh6aAuc21Vwd',
    motorcycle: 'tmpl_kfaFkGugqG9jqh6aAuc21Vwd',
    bicycle: 'tmpl_dos19dD2bQ9wjrnVjAWRo1Ff',
    walking: 'tmpl_dos19dD2bQ9wjrnVjAWRo1Ff',
    other: 'tmpl_dos19dD2bQ9wjrnVjAWRo1Ff',
  },
}

const SCAN_DL_BACK_TEMPLATE_ID = 'tmpl_WocVBd1qwFoAQQXx6V9SRpSK'

export const IdvStep = {
  Start: 'start',
  Complete: 'complete',
  Fail: 'fail',
}

const determineTemplateId = (vehicleType: string, isIDVReferral) => {
  const channel = isIDVReferral ? IdvChannel.REFERRAL : IdvChannel.NON_REFERRAL
  if (
    vehicleType == VehicleType.Bicycle ||
    vehicleType == VehicleType.Walking
  ) {
    return TemplateIds[channel][vehicleType]
  }

  if (isFeatureFlagEnabled(Exp.SCAN_DL_BACK)) {
    return SCAN_DL_BACK_TEMPLATE_ID
  }
  return TemplateIds[channel][vehicleType]
}

const isResumeInquiry = (
  vehicleType: string,
  inquiryId: string,
  idvStatus: string,
  idvTemplateId: string,
  isIDVReferral: boolean
) => {
  const channel = isIDVReferral ? IdvChannel.REFERRAL : IdvChannel.NON_REFERRAL
  if (
    TemplateIdTypes['non-motorized'].includes(idvTemplateId) &&
    TemplateIdTypes.motorized.includes(TemplateIds[channel][vehicleType])
  ) {
    return false
  }

  return Boolean(inquiryId) && idvStatus !== IDVStatus.APPROVED
}

type PersonaEvent =
  | 'load'
  | 'start'
  | 'country-select'
  | 'document-camera-select'
  | 'document-camera-capture'
  | 'document-upload'
  | 'verification-select'
  | 'verification-change'
  | 'success'
  | 'fail'
  | 'exit'
  | 'selfie-camera-select'
  | 'selfie-camera-capture'

const isProd = isProduction(fromDoorstepInjectedEnvironment(window))

const getPersonaLocale = ddLocale => {
  const langMap = {
    [Locale.FR_CA]: 'fr',
    [Locale.ES_US]: 'es-MX',
    [Locale.JA_JP]: 'ja',
    [Locale.DE_DE]: 'de',
    [Locale.EN_CA]: 'en-US',
    [Locale.EN_AU]: 'en-US',
    [Locale.EN_NZ]: 'en-US',
  }
  return langMap[ddLocale] || 'en-US'
}
const IdentityVerification: React.FC<Props> = ({
  uniqueLink,
  vehicleType,
  onUpdate,
  trackIdentityVerificationSteps,
  updateIdvStatus,
  renderToggle,
  email,
  isIDVReferral,
  application,
}) => {
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    const loadPersona = async () => {
      let sessionToken
      let inquiryId = get(application, 'identityInquiryId', undefined)
      const idvStatus = get(
        application,
        'identityVerificationStatus',
        IDVStatus.NOT_COMPLETED
      )
      const idvTemplateId = get(
        application,
        'identityTemplateId',
        IDVStatus.NOT_COMPLETED
      )
      if (isFeatureFlagEnabled(Exp.IDV_SESSION_LIMIT)) {
        if (
          isResumeInquiry(
            vehicleType,
            inquiryId,
            idvStatus,
            idvTemplateId,
            isIDVReferral
          )
        ) {
          const response = await getIdvSessionToken(uniqueLink, inquiryId)
          trackIdentityVerificationSteps({
            status: EventActions.Success,
            step: IdentityVerificationPages.ResumeInquiry,
          })
          sessionToken = camelCaseObjectKeys(response.data).sessionToken
        }
      }

      // TemplateId, InquiryId, and prefill Params should be sent on creating or updating new Inquiry Id
      // https://docs.withpersona.com/docs/parameters

      const templateId = !sessionToken
        ? determineTemplateId(vehicleType, isIDVReferral)
        : null
      inquiryId = sessionToken ? inquiryId : null
      const prefillParams = !sessionToken
        ? {
            emailAddress: email,
          }
        : null

      const accessToken = sessionToken
      const client = new Persona.Client({
        templateId,
        inquiryId,
        accessToken,
        environment: isProd ? PERSONA_ENV.Production : PERSONA_ENV.Sandbox,
        language: getPersonaLocale(getCurrentLocale.getCurrentLocale()),
        referenceId: uniqueLink,
        prefill: prefillParams,
        onLoad: () => {
          setIsLoading(false)
          client.open()
        },
        onStart: async (inquiryId: string) => {
          await updateIdvStatus(uniqueLink, inquiryId, IdvStep.Start, false)
        },
        onComplete: async (inquiryId: string) => {
          setIsLoading(true)
          await updateIdvStatus(uniqueLink, inquiryId, IdvStep.Complete, true)
          setIsLoading(false)
          onUpdate({ idVerificationStatus: IDVStatus.SUCCESS })
        },
        onFail: async (inquiryId: string) => {
          setIsLoading(true)
          await updateIdvStatus(uniqueLink, inquiryId, IdvStep.Fail, true)
          setIsLoading(false)
          onUpdate({ idVerificationStatus: IDVStatus.FAILED })
        },
        onExit: () => {
          onUpdate({
            idVerificationStatus: IDVStatus.NOT_COMPLETED,
            renderToggle: !renderToggle,
          })
        },
        onEvent: (eventName: PersonaEvent, metadata: any) => {
          trackIdentityVerificationSteps({
            status: metadata && metadata.status,
            step: eventName,
            email,
          })
        },
      })
    }

    loadPersona()
    // TODO @tanu-doordash: revisit application logic and remove eslint-disabling below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderToggle])

  if (isLoading) {
    return <Loading />
  }

  return null
}

const mapStateToProps = state => ({
  idvStatus: state.idvStatus,
  idvSessionToken: state.idvSessionToken,
  application: state.application,
})

const mapDispatchToProps = {
  updateIdvStatus: actions.updateIdvStatus,
}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(IdentityVerification)
